import {Loader, UpdateCallback} from '@gongxigames/framework/core/services/resourceLoader/Loader';
import {consoleLogger} from '@gongxigames/framework/core/fragments/ConsoleLogger';
import {IBrowser} from '@gongxigames/framework/core/types';

export interface FontLoaderParams {
    family: string;
    fileName: string;
    dir: string;

    // 'normal' | 'bold' | 'bolder' | 'lighter'
    weight?: string;

    formats?: string[];
}

export class FontLoader extends Loader {

    private static readonly ERROR_LOADING_TIME: number = 3000;
    private static readonly INTERVAL: number = 50;

    private _family: string;
    private _dir: string;
    private _fileName: string;

    // 'normal' | 'bold' | 'bolder' | 'lighter'
    private _weight: string;

    private _formats: string[];
    private _loadingTime = 0;
    private _browser: IBrowser;

    constructor(params: FontLoaderParams, size: number,
        callback: Function, updateCallback: UpdateCallback, errorCallback: Function, browser: IBrowser) {
        super(callback, updateCallback, errorCallback, size);

        this._dir = params.dir;
        this._fileName = params.fileName;
        this._family = params.family || this._fileName;
        this._weight = params.weight || 'normal';
        this._formats = params.formats || ['woff2', 'woff', 'ttf'];
        this._browser = browser;
    }

    public load(): void {
        this.createFontFace();
        this.loadFont();
    }

    protected loadFont(): void {
        const spanEl: HTMLElement = document.createElement('span');

        document.body.appendChild(spanEl);
        spanEl.style.font = `10000px ${this._family}`;
        spanEl.style.fontWeight = this._weight;
        spanEl.style.position = 'absolute';
        spanEl.style.visibility = 'hidden';
        spanEl.textContent = 'M';

        const offsetHeight = spanEl.offsetHeight;
        const intervalId = this._browser.addInterval(() => {
            this._loadingTime += FontLoader.INTERVAL;

            if (this._loadingTime > FontLoader.ERROR_LOADING_TIME) {
                consoleLogger.warn(`Font ${this._fileName} isn't loaded`);
                this.onLoaded(spanEl, intervalId);

                return;
            }

            if (spanEl.offsetHeight !== offsetHeight) {
                this.onLoaded(spanEl, intervalId);
            }
        }, FontLoader.INTERVAL);
    }

    protected onLoaded(spanEl: HTMLElement, intervalId: number): void {
        spanEl.parentElement?.removeChild(spanEl);
        this._browser.removeInterval(intervalId);

        this._loadedPercent = 1;
        this._updateCallback && this._updateCallback(this._loadedPercent);
        this._callback && this._callback();
    }

    /**
     * @font-face {
     *   font-family: mangal;
     *   font-weight: bold;
     *   src: url() format(''),
     *       url() format(''),
     *       url() format('');
     *   }
     */
    protected createFontFace(): void {
        const styleNode = document.createElement('style');
        const srcStrings: string[] = [];

        styleNode.type = 'text/css';
        styleNode.id = this._fileName;

        for (const format of this._formats) {
            srcStrings.push(`url('${this._dir}/${this._fileName}.${format}') format('${format}')`);
        }

        const styleNodeText = `@font-face {
            font-family: ${this._family};
            font-weight: ${this._weight};
            src: ${srcStrings.join(', ')};
        }`;

        styleNode.appendChild(document.createTextNode(styleNodeText));
        document.getElementsByTagName('head')[0].appendChild(styleNode);
    }

    protected onProgress(e: any): void {
    }
}
